import { isInt, isNumberString } from 'class-validator';
import * as React from 'react';

import { UnifiedRolesEditorPolicy } from '@project44/iam-team-ui-utils/src/domains/UnifiedRoles/domain/abstract';
import * as ManifestUI from '@project44/manifest-ds';
import { FetchError, User } from '@project44/ui-utils';
import { SharedModels, UserEnums } from '@project44-models/iam-gateway';

import {
  ITableFactoryConstructor,
  TTableFactoryCallBack,
} from '../../../../../external/UILibrary/domain/interfaces';
import { TypographyComponent } from '../../../../../shared/core/infrastructrue/ui/Typography/Typography.component';
import {
  IUserUnifiedRolesTableComposer,
  IUserUnifiedRolesTableComposerConstructor,
  TAssignedUserTenantAgnosticRolesState,
  TAssignedUserTenantSpecificRolesState,
} from '../../../domain/interfaces';
import { UserI18NPort } from '../../ports/User.i18n.port';

export class UserUnifiedRolesTable implements IUserUnifiedRolesTableComposer {
  private constructor(
    private readonly payload: IUserUnifiedRolesTableComposerConstructor,
  ) {}

  public static create(
    config: IUserUnifiedRolesTableComposerConstructor,
  ): UserUnifiedRolesTable {
    return new UserUnifiedRolesTable(config);
  }

  public composeUserUnifiedRolesDataState(payload: {
    isLoading: boolean;
    error: undefined | FetchError;
    data: Array<SharedModels.UnifiedRoleViewModel>;
  }): ITableFactoryConstructor<SharedModels.UnifiedRoleViewModel>['resourceDataState'] {
    return {
      resourceCollection: payload.data,
      error: payload.error,
      maybeIsLoading: payload.isLoading,
    };
  }

  public readonly compose: IUserUnifiedRolesTableComposer['compose'] = ({
    assignedUserTenantSpecificRolesState,
    dataState,
    assignedUserTenantAgnosticRolesState,
    userTenantAgnosticRolesEditor,
    userTenantSpecificRolesEditor,
    shouldDisplayTenantAgnosticRoles,
    userPrincipal,
    featureFlags,
  }) => {
    const { maybeIsLoading, error, resourceCollection } =
      this.composeUserUnifiedRolesDataState(dataState);

    const resourceDataState = {
      maybeIsLoading,
      error,
      resourceCollection: this.filterRolesFromUITable(
        resourceCollection,
        userTenantAgnosticRolesEditor,
        featureFlags,
      ),
    };

    const composerProps = {
      resourceDataState,
      userTenantSpecificRolesEditor,
    };

    return {
      sortingConfig: ['name', UserEnums.LowercasePaginationSortEnum.ASC],
      placeholderCellsWhileDataLoads: 20,
      shouldDisplayTableHeaders: true,
      properties: [
        ['category', this.composeTenantTypeColumn()],
        ['name', this.composeTenantRoleNameColumn(composerProps)],
        [
          'tenantSpecific',
          this.composeTenantSpecificRoleColumn({
            ...composerProps,
            userPrincipal,
            assignedUserTenantAgnosticRolesState,
            assignedUserTenantSpecificRolesState,
          }),
        ],
        ...(shouldDisplayTenantAgnosticRoles
          ? new Map([
              [
                'tenantAgnostic',
                this.composeTenantAgnosticRoleColumn({
                  resourceDataState,
                  userPrincipal,
                  userTenantAgnosticRolesEditor,
                  assignedUserTenantAgnosticRolesState,
                }),
              ],
            ])
          : new Map()),
      ],
      resourceDataState,
    };
  };

  public readonly composeTenantSpecificRoleColumn = (payload: {
    assignedUserTenantAgnosticRolesState: TAssignedUserTenantAgnosticRolesState;
    assignedUserTenantSpecificRolesState: TAssignedUserTenantSpecificRolesState;
    resourceDataState: ITableFactoryConstructor<SharedModels.UnifiedRoleViewModel>['resourceDataState'];
    userTenantSpecificRolesEditor: UnifiedRolesEditorPolicy;
    userPrincipal: User;
  }): TTableFactoryCallBack<SharedModels.UnifiedRoleViewModel> => {
    const {
      assignedUserTenantAgnosticRolesState,
      resourceDataState,
      assignedUserTenantSpecificRolesState,
      userTenantSpecificRolesEditor,
      userPrincipal,
    } = payload;

    const [availableTenantAgnosticRoles] = assignedUserTenantAgnosticRolesState;

    return ({ resource }) => ({
      tableHeaderColumnProps: {
        css: {
          minWidth: '170px',
        },
        children: (
          <this.payload.composeUserTenantSpecificRolesSelectAllCheckbox
            {...{
              isApiRequestInProgress: resourceDataState.maybeIsLoading,
              userPrincipal,
              userTenantSpecificRolesEditor,
              assignedUserTenantSpecificRolesState,
              availableTenantAgnosticRoles,
            }}
          />
        ),
      },
      tableCellProps: {
        css: {
          verticalAlign: 'text-top',
        },
        children: (
          <this.payload.composeUserTenantSpecificRolesCheckbox
            {...{
              isApiRequestInProgress: resourceDataState.maybeIsLoading,
              unifiedRole: resource,
              userPrincipal,
              userTenantSpecificRolesEditor,
              assignedUserTenantSpecificRolesState,
              availableTenantAgnosticRoles,
            }}
          />
        ),
      },
      tableRowProps: {},
    });
  };

  public readonly composeTenantAgnosticRoleColumn = (payload: {
    assignedUserTenantAgnosticRolesState: TAssignedUserTenantAgnosticRolesState;
    resourceDataState: ITableFactoryConstructor<SharedModels.UnifiedRoleViewModel>['resourceDataState'];
    userTenantAgnosticRolesEditor: UnifiedRolesEditorPolicy;
    userPrincipal: User;
  }): TTableFactoryCallBack<SharedModels.UnifiedRoleViewModel> => {
    const {
      resourceDataState,
      assignedUserTenantAgnosticRolesState,
      userTenantAgnosticRolesEditor,
      userPrincipal,
    } = payload;

    return ({ resource }) => ({
      tableHeaderColumnProps: {
        css: {
          minWidth: '260px',
        },
        children: (
          <this.payload.composeUserTenantAgnosticRolesSelectAllCheckbox
            {...{
              isApiRequestInProgress: resourceDataState.maybeIsLoading,
              userPrincipal,
              userTenantAgnosticRolesEditor,
              assignedUserTenantAgnosticRolesState,
            }}
          />
        ),
      },
      tableCellProps: {
        css: {
          verticalAlign: 'text-top',
        },
        children: (
          <this.payload.composeUserTenantAgnosticRolesCheckbox
            {...{
              isApiRequestInProgress: resourceDataState.maybeIsLoading,
              unifiedRole: resource,
              userPrincipal,
              userTenantAgnosticRolesEditor,
              assignedUserTenantAgnosticRolesState,
            }}
          />
        ),
      },
      tableRowProps: {},
    });
  };

  public readonly composeTenantRoleNameColumn = (payload: {
    resourceDataState: ITableFactoryConstructor<SharedModels.UnifiedRoleViewModel>['resourceDataState'];
    userTenantSpecificRolesEditor: UnifiedRolesEditorPolicy;
  }): TTableFactoryCallBack<SharedModels.UnifiedRoleViewModel> => {
    const { resourceDataState } = payload;

    const withLoadingStatusOptions = {
      isLoading: resourceDataState.maybeIsLoading,
    };

    return ({ resource }) => ({
      tableHeaderColumnProps: {
        css: {
          width: '100%',
        },
        isSortable: true,
        children: TypographyComponent.compose({
          typographyProps: {
            variant: 'subtextBold',
            content: UserI18NPort.scopedTranslator('userRolesTable.role'),
          },
          withLoadingStatusOptions,
        }),
      },
      tableCellProps: {
        tableCellRowData: resource,
        css: {
          whiteSpace: 'pre-wrap',
        },
        children: [
          TypographyComponent.compose({
            typographyProps: {
              variant: 'subtextBold',
              role: 'presentation',
              'aria-label': `user-role-data-cell_${resource.name}_display-value`,
              content: isNumberString(resource.id)
                ? resource.name
                : `NMC: ${resource.name}`,
            },
            withLoadingStatusOptions,
          }),
          TypographyComponent.compose({
            typographyProps: {
              css: {
                display: 'flex',
                textWrap: 'wrap',
              },
              variant: 'caption',
              role: 'presentation',
              'aria-label': `user-role-subtext-cell_${resource.name}_display-value`,
              content: resource.description,
            },
            withLoadingStatusOptions,
          }),
        ],
      },
      tableRowProps: {},
    });
  };

  public readonly composeTenantTypeColumn =
    (): TTableFactoryCallBack<SharedModels.UnifiedRoleViewModel> => {
      return ({ resource }) => ({
        tableHeaderColumnProps: {
          isSortable: true,
          children: TypographyComponent.compose({
            typographyProps: {
              variant: 'subtextBold',
              content: UserI18NPort.scopedTranslator('userRolesTable.type'),
            },
          }),
        },
        tableCellProps: {
          css: {
            verticalAlign: 'text-top',
          },
          tableCellRowData: resource,
          children: (
            <ManifestUI.Tooltip
              title={resource.category}
              children={<ManifestUI.Tag>{resource.category}</ManifestUI.Tag>}
            />
          ),
        },
        tableRowProps: {},
      });
    };

  public filterRolesFromUITable(
    unifiedRoles: Array<SharedModels.UnifiedRoleViewModel>,
    userTenantAgnosticRolesEditor: UnifiedRolesEditorPolicy,
    featureFlags: Record<string | number | symbol, boolean>,
  ): Array<SharedModels.UnifiedRoleViewModel> {
    const {
      checkUnifiedRoleIsTenantAdminUnifiedRole,
      checkUnifiedRoleIsNMCLegacyReadOnlyUnifiedRole,
      checkUnifiedRoleIsNMCLegacyTenantAdminUnifiedRole,
    } = userTenantAgnosticRolesEditor;

    return unifiedRoles
      .filter((role) => {
        switch (true) {
          case checkUnifiedRoleIsTenantAdminUnifiedRole(role): {
            return null;
          }
          case checkUnifiedRoleIsNMCLegacyReadOnlyUnifiedRole(role): {
            return null;
          }
          case checkUnifiedRoleIsNMCLegacyTenantAdminUnifiedRole(role): {
            return null;
          }
          case this.payload.shouldDisableNMCRolesSupport(featureFlags): {
            return isInt(Number(role.id)) ? role : null;
          }
          default:
            return role;
        }
      })
      .filter(Boolean);
  }
}
